import type { Backdrop, BirdLook, HumanLook, Opponent, SpecialId } from "../types.ts";

export function bird(o: Partial<BirdLook> = {}): BirdLook {
  return {
    kind: "bird",
    body: "#f5f1e6",
    wing: "#e6dcc6",
    tail: "#d9cdb2",
    comb: "#d8342c",
    beak: "#f2b134",
    legs: "#f2b134",
    combStyle: "single",
    tailStyle: "fluffy",
    pattern: "solid",
    eyes: "normal",
    extra: "none",
    beakStyle: "pointy",
    wattle: true,
    size: 1,
    neck: 1,
    legLen: 1,
    shape: "round",
    ...o,
  };
}

function human(o: Partial<HumanLook>): HumanLook {
  return {
    kind: "human",
    skin: "#f0c39b",
    hair: "#5a3b22",
    shirt: "#d9d4c8",
    pants: "#3b4a6b",
    hat: "none",
    beard: "none",
    glasses: false,
    size: 1,
    ...o,
  };
}

export interface Line {
  /** "hero", "narrator", or an opponent id. */
  who: string;
  text: string;
}

export interface Chapter {
  title: string;
  subtitle: string;
  region: string;
  backdrop: Backdrop;
  intro: Line[];
  fights: Opponent[];
  outro: Line[];
  reward: { gold: number; special?: SpecialId; item?: string };
}

export const PROLOGUE: Line[] = [
  { who: "narrator", text: "Since the dawn of time, the humans have taken our eggs." },
  { who: "narrator", text: "They put us in coops. They put us in buckets. They put us in... nuggets." },
  { who: "narrator", text: "But on Dale's Farm, one chicken has finally had enough." },
  { who: "hero", text: "No more! Today the coop rises. Tomorrow... THE WORLD!" },
  { who: "narrator", text: "First, though, there is the small matter of the pecking order." },
];

export const CHAPTERS: Chapter[] = [
  {
    title: "Chapter I",
    subtitle: "The Pecking Order",
    region: "Dale's Farm",
    backdrop: "barn",
    intro: [
      { who: "narrator", text: "Dale's Farm. 5:00 AM. The feed is late again." },
      { who: "big-red", text: "Who's the scrawny bird talkin' revolution? Nobody leads MY coop without fightin' through the pecking order!" },
      { who: "hero", text: "Then I'll fight my way to the top. One beak at a time." },
    ],
    fights: [
      {
        id: "little-nugget",
        name: "Little Nugget",
        title: "Bottom of the Pecking Order",
        level: 1,
        archetype: "duelist",
        look: bird({ body: "#f7d54a", wing: "#ecc237", tail: "#e3b02c", comb: "#f08a4b", combStyle: "none", wattle: false, size: 0.78, beakStyle: "short" }),
        gear: { weapon: "twig" },
        taunt: "I may be small, but I've been pecking since Tuesday!",
      },
      {
        id: "gertrude",
        name: "Gertrude",
        title: "The Goose Enforcer",
        level: 2,
        archetype: "brute",
        look: bird({ body: "#f4f4ee", wing: "#dcdcd2", tail: "#cfcfc4", beak: "#f08a24", legs: "#f08a24", combStyle: "none", wattle: false, beakStyle: "duck", tailStyle: "stub", neck: 1.9, size: 1.08, eyes: "fierce" }),
        gear: { weapon: "corncob" },
        taunt: "HONK. This is MY barnyard. HONK HONK.",
      },
      {
        id: "big-red",
        name: "Big Red",
        title: "King of the Coop",
        level: 3,
        archetype: "brute",
        look: bird({ body: "#a63a1e", wing: "#6b2410", tail: "#1f2b3a", tailStyle: "sickle", eyes: "fierce", size: 1.15, extra: "scar" }),
        gear: { weapon: "spork", shield: "potlid", helmet: "eggshell" },
        taunt: "Every bird in this coop answers to Big Red!",
      },
      {
        id: "farmer-dale",
        name: "Farmer Dale",
        title: "Owner of Dale's Farm",
        level: 4,
        archetype: "balanced",
        boss: true,
        power: 1.12,
        look: human({ hat: "straw", beard: "stubble", overalls: true, shirt: "#c8443a", pants: "#3e6aa8", hair: "#7a5230" }),
        gear: { weapon: "pitchfork" },
        taunt: "What in tarnation?! Get back in that coop before I make you into nuggets!",
      },
    ],
    outro: [
      { who: "narrator", text: "Farmer Dale fled to town in his pickup truck, still wearing his pajama bottoms." },
      { who: "hero", text: "The farm is OURS! Sisters, brothers: lay no more eggs for the humans!" },
      { who: "narrator", text: "The hens raided the kitchen. {name} discovered that eggs make excellent grenades." },
      { who: "narrator", text: "NEW FOWL ART: Egg Grenade. Hurl an egg at any range. Dale also left his pitchfork behind." },
      { who: "hero", text: "Next stop: the County Fair. They judge chickens there. Time to judge them back." },
    ],
    reward: { gold: 150, special: "egg", item: "pitchfork" },
  },
  {
    title: "Chapter II",
    subtitle: "Fowl Play at the Fair",
    region: "Cluckington County",
    backdrop: "fair",
    intro: [
      { who: "narrator", text: "The Cluckington County Fair. Funnel cakes. A Ferris wheel. A 'Best in Show' poultry tent." },
      { who: "hero", text: "They lock my cousins in cages and hand out ribbons. Disgusting." },
      { who: "duck-norris", text: "This fair is protected by Quack-Fu. Turn back, chicken." },
    ],
    fights: [
      {
        id: "duck-norris",
        name: "Duck Norris",
        title: "Black Belt in Quack-Fu",
        level: 5,
        archetype: "duelist",
        look: bird({ body: "#9a8f80", belly: "#a8663f", wing: "#6e5b48", tail: "#3a3530", head: "#1f6b3a", beak: "#e6c22e", legs: "#f08a24", combStyle: "none", wattle: false, beakStyle: "duck", tailStyle: "stub", eyes: "fierce", extra: "bandana", extraColor: "#d32f2f" }),
        gear: { weapon: "butterknife", boots: "sandals" },
        taunt: "I don't do push-ups. I push the pond down.",
      },
      {
        id: "tom-turkington",
        name: "Tom Turkington",
        title: "Three-Time Blue Ribbon Turkey",
        level: 6,
        archetype: "tank",
        look: bird({ body: "#4a3426", wing: "#6b4a32", tail: "#5c3c26", head: "#d97a7a", comb: "#c0262a", combStyle: "snood", beak: "#c9b48a", legs: "#c96a5a", tailStyle: "fan", pattern: "barred", patternColor: "#2a1c14", size: 1.25, extra: "bowtie", extraColor: "#2f6fd6" }),
        gear: { weapon: "corncob", shield: "buckler", armor: "sack" },
        taunt: "Gobble gobble. That's the sound of you losing.",
      },
      {
        id: "hen-solo",
        name: "Hen Solo",
        title: "Smuggler of the Midway",
        level: 7,
        archetype: "showman",
        look: bird({ body: "#c9853a", wing: "#9a5b22", tail: "#2b2b33", pattern: "laced", patternColor: "#5a3412", combStyle: "rose" }),
        gear: { weapon: "gladius", armor: "jerkin", boots: "sandals" },
        taunt: "Never tell me the odds. Especially not at the egg toss.",
      },
      {
        id: "sheriff",
        name: "Sheriff McGraw",
        title: "The Law in Cluckington",
        level: 8,
        archetype: "balanced",
        boss: true,
        power: 1.12,
        look: human({ hat: "cowboy", beard: "mustache", badge: true, shirt: "#c9a46a", jacket: "#6b4325", pants: "#3a3a44", hair: "#3a2a1e" }),
        gear: { weapon: "hatchet", shield: "bronzeshield", armor: "jerkin" },
        taunt: "This county ain't big enough for the both of us, featherbrain.",
      },
    ],
    outro: [
      { who: "narrator", text: "The Sheriff's badge now hangs above the coop door. Cluckington County has fallen." },
      { who: "narrator", text: "Chickens across the county break out of their coops and pledge themselves to {name}." },
      { who: "narrator", text: "NEW FOWL ART: Rooster Roar. A battle cry so terrifying it can stun your foe." },
      { who: "hero", text: "Next: the Big City. Where the nuggets are made." },
    ],
    reward: { gold: 400, special: "roar" },
  },
  {
    title: "Chapter III",
    subtitle: "Concrete Coop",
    region: "Metro City",
    backdrop: "city",
    intro: [
      { who: "narrator", text: "Metro City. Eight million humans. Four hundred fried chicken restaurants." },
      { who: "hero", text: "Four hundred... and soon ZERO." },
      { who: "pigeon-capone", text: "Hey! Farm bird! The city's got its own birds, see? And we don't take kindly to tourists." },
    ],
    fights: [
      {
        id: "pigeon-capone",
        name: "Pigeon Capone",
        title: "Don of the Docks",
        level: 9,
        archetype: "duelist",
        look: bird({ body: "#8a93a3", wing: "#6c7585", tail: "#4a5262", head: "#5b6d8a", beak: "#3a3a3a", legs: "#d96a7a", combStyle: "none", wattle: false, beakStyle: "short", size: 0.95, extra: "mustache", pattern: "barred", patternColor: "#4a5262" }),
        gear: { weapon: "cleaver", armor: "jerkin", helmet: "saucepan" },
        taunt: "You're on my turf, see? Nobody coos around here without my say-so.",
      },
      {
        id: "sir-quacks",
        name: "Sir Quacks-a-Lot",
        title: "Knight of the Duck Pond",
        level: 10,
        archetype: "tank",
        look: bird({ body: "#f4f4ee", wing: "#e2e2d8", tail: "#d2d2c6", beak: "#f2a531", legs: "#f2a531", combStyle: "none", wattle: false, beakStyle: "duck", tailStyle: "stub" }),
        gear: { weapon: "gladius", shield: "kite", helmet: "galea", armor: "bronzeplate" },
        taunt: "Have at thee, foul fowl!",
      },
      {
        id: "seagull-steve",
        name: "Seagull Steve",
        title: "Thief of a Thousand Fries",
        level: 11,
        archetype: "showman",
        look: bird({ body: "#f4f6f8", wing: "#9aa6b2", tail: "#2b2f36", beak: "#f2c12e", legs: "#f2c12e", combStyle: "none", wattle: false, beakStyle: "hooked", tailStyle: "stub", eyes: "fierce" }),
        gear: { weapon: "pike", helmet: "viking", boots: "leathergreaves" },
        taunt: "Mine. Mine. MINE. ...Mine?",
      },
      {
        id: "mayor",
        name: "Mayor Pembrooke",
        title: "Elected (Mostly)",
        level: 12,
        archetype: "showman",
        boss: true,
        power: 1.12,
        look: human({ hat: "top", glasses: true, jacket: "#2c2c3a", shirt: "#f2f2f2", pants: "#2c2c3a", tie: "#7a1f3d", sash: "#d4a017", hair: "#b8b8b8", skin: "#e8b48c" }),
        gear: { weapon: "mace", shield: "kite", armor: "chainmail" },
        taunt: "I'll have you know I was ELECTED. Mostly.",
      },
    ],
    outro: [
      { who: "narrator", text: "The Mayor resigned live on the evening news, to a chorus of triumphant clucking." },
      { who: "narrator", text: "Every pigeon, seagull and city duck now answers to {name}." },
      { who: "narrator", text: "NEW FOWL ART: Feather Frenzy. Three lightning-fast strikes in a single turn." },
      { who: "hero", text: "One city isn't enough. To the Capital!" },
    ],
    reward: { gold: 900, special: "frenzy" },
  },
  {
    title: "Chapter IV",
    subtitle: "Capitol Cluck",
    region: "The Nation",
    backdrop: "capitol",
    intro: [
      { who: "narrator", text: "The Capital. The government has declared a State of Poultry Emergency." },
      { who: "general-gobbler", text: "ATTENTION! The armed forces of this nation will NOT be defeated by a chicken!" },
      { who: "hero", text: "Funny. That's exactly what the last four bosses said." },
    ],
    fights: [
      {
        id: "agent-mallard",
        name: "Agent Mallard",
        title: "Classified Waterfowl",
        level: 13,
        archetype: "duelist",
        look: bird({ body: "#9a9a96", belly: "#8a5a3c", wing: "#5f5a52", tail: "#2a2a2a", head: "#17613a", beak: "#e6c22e", legs: "#f08a24", combStyle: "none", wattle: false, beakStyle: "duck", tailStyle: "stub", eyes: "shades", extra: "bowtie", extraColor: "#111111" }),
        gear: { weapon: "longsword", armor: "chainmail", boots: "bronzegreaves" },
        taunt: "The name's Mallard. Agent Mallard. Shaken, not stir-fried.",
      },
      {
        id: "general-gobbler",
        name: "General Gobbler",
        title: "Five-Star Turkey",
        level: 14,
        archetype: "tank",
        look: bird({ body: "#3e2c20", wing: "#5d4130", tail: "#4d3424", head: "#d97a7a", comb: "#c0262a", combStyle: "snood", beak: "#c9b48a", legs: "#c96a5a", tailStyle: "fan", pattern: "barred", patternColor: "#22170f", size: 1.3, eyes: "fierce" }),
        gear: { weapon: "warhammer", armor: "chainmail", helmet: "bascinet", boots: "bronzegreaves" },
        taunt: "Drop and give me twenty, private! Then I'll drop YOU!",
      },
      {
        id: "liberty",
        name: "Liberty",
        title: "The Bald Eagle",
        level: 15,
        archetype: "brute",
        look: bird({ body: "#5a3a22", wing: "#4a2e18", tail: "#f4f4f0", head: "#f7f7f2", beak: "#f2c12e", legs: "#f2c12e", combStyle: "none", wattle: false, beakStyle: "hooked", tailStyle: "fluffy", eyes: "fierce", size: 1.2 }),
        gear: { weapon: "trident", helmet: "galea", boots: "sabatons" },
        taunt: "*Majestic screeching*",
      },
      {
        id: "president",
        name: "President Huxley",
        title: "Leader of the Free World",
        level: 16,
        archetype: "balanced",
        boss: true,
        power: 1.02,
        look: human({ jacket: "#1f2a44", shirt: "#ffffff", pants: "#1f2a44", tie: "#c62828", hair: "#d9c9a3", skin: "#f2c6a0" }),
        gear: { weapon: "longsword", shield: "kite", armor: "chainmail" },
        taunt: "My fellow citizens... are about to watch me carve a chicken.",
      },
    ],
    outro: [
      { who: "narrator", text: "In a historic address, the President recognized {name} as Supreme Leader of the Nation." },
      { who: "narrator", text: "Eggs are now legal tender. The national anthem is now just clucking." },
      { who: "narrator", text: "NEW FOWL ART: Second Wind. Restore a big chunk of health once per fight." },
      { who: "hero", text: "One nation down. One hundred and ninety-four to go. Or... one summit." },
    ],
    reward: { gold: 2000, special: "wind" },
  },
  {
    title: "Chapter V",
    subtitle: "World Domination",
    region: "The World",
    backdrop: "summit",
    intro: [
      { who: "narrator", text: "Geneva. The Emergency World Summit on the Chicken Situation." },
      { who: "narrator", text: "Earth's mightiest champions have gathered to stop {name}. Behind them lurks a figure in a white suit..." },
      { who: "colonel", text: "I've been frying your kind for seventy years, {name}. You're just another drumstick." },
      { who: "hero", text: "Then come and get me, Colonel." },
    ],
    fights: [
      {
        id: "le-coq",
        name: "Le Coq Magnifique",
        title: "Champion of France",
        level: 17,
        archetype: "showman",
        look: bird({ body: "#2b4fa3", wing: "#f2f2f2", tail: "#d8342c", tailStyle: "sickle", extra: "mustache", size: 1.1 }),
        gear: { weapon: "longsword", helmet: "galea", armor: "chainmail", boots: "sabatons", shield: "kite" },
        taunt: "Hon hon hon! Prepare to be... how you say... flambéed!",
      },
      {
        id: "emperor-pengwin",
        name: "Emperor Pengwin",
        title: "Lord of Antarctica",
        level: 18,
        archetype: "tank",
        look: bird({ shape: "penguin", body: "#1d2230", belly: "#f4f4f0", wing: "#1d2230", tail: "#1d2230", head: "#1d2230", beak: "#f2a531", legs: "#f2a531", legLen: 0.45, combStyle: "none", wattle: false, beakStyle: "short", tailStyle: "stub", size: 1.1 }),
        gear: { weapon: "scythe", helmet: "crown", armor: "chainmail", boots: "sabatons" },
        taunt: "The ice remembers every chicken who crossed it. Coldly.",
      },
      {
        id: "ozzy",
        name: "Ozzy",
        title: "The Outback Ostrich",
        level: 19,
        archetype: "brute",
        look: bird({ body: "#2a2a2e", wing: "#f2f2ee", tail: "#f2f2ee", head: "#cbb6a8", beak: "#d8a88c", legs: "#cf9f8f", combStyle: "none", wattle: false, beakStyle: "duck", neck: 2.3, legLen: 1.8, size: 1.12, eyes: "fierce" }),
        gear: { weapon: "execaxe", helmet: "viking", boots: "wingedtalons", armor: "chainmail" },
        taunt: "G'day, mate. Ever been kicked by a dinosaur?",
      },
      {
        id: "colonel",
        name: "The Colonel",
        title: "Fried Chicken Tycoon",
        level: 21,
        archetype: "balanced",
        boss: true,
        power: 1.04,
        look: human({ jacket: "#f4f4f0", shirt: "#ffffff", pants: "#f4f4f0", tie: "#111111", hair: "#f4f4f4", beard: "goatee", glasses: true, skin: "#f2c6a0", size: 1.05 }),
        gear: { weapon: "drumstick", armor: "regalia" },
        taunt: "Eleven herbs and spices, {name}. And you're the twelfth.",
      },
    ],
    outro: [
      { who: "narrator", text: "The Colonel's secret recipe was burned. All eleven herbs AND spices." },
      { who: "narrator", text: "Across the globe, coops swung open. Buckets were outlawed. Nuggets became a crime against chickenity." },
      { who: "hero", text: "From a dusty coop on Dale's Farm... to the throne of the world." },
      { who: "narrator", text: "All hail {name}, Supreme Overlord of Planet Earth!" },
    ],
    reward: { gold: 5000 },
  },
];

export const DEFEAT_LINES: Record<string, string> = {
  "little-nugget": "Okay, okay! You can have my spot at the feeder!",
  gertrude: "Honk... (that's goose for 'I yield').",
  "big-red": "The coop... is yours. Long live the new boss.",
  "farmer-dale": "My chickens... have UNIONIZED?!",
  "duck-norris": "My first defeat... since hatching.",
  "tom-turkington": "Please... don't tell the judges.",
  "hen-solo": "I know.",
  sheriff: "Reckon this county's under new management.",
  "pigeon-capone": "Fuggedaboutit... the city's yours.",
  "sir-quacks": "'Tis but a scratch. And a broken everything.",
  "seagull-steve": "Mine... (not mine).",
  mayor: "I'd like to formally announce my resignation.",
  "agent-mallard": "Mission... failed. Tell my ducklings I tried.",
  "general-gobbler": "Retreat! RETREAT!",
  liberty: "*Noticeably less majestic screeching*",
  president: "I concede. Somebody get me a lawyer.",
  "le-coq": "Zut alors! I surrender. As is tradition.",
  "emperor-pengwin": "Brrr... the South Pole is yours.",
  ozzy: "Strewth... I'll go bury me head in the sand.",
  colonel: "Impossible... my secret recipe... plucked...",
};

export const STORY_FIGHTS: { chapter: number; opponent: Opponent }[] = CHAPTERS.flatMap((c, chapter) =>
  c.fights.map((opponent) => ({ chapter, opponent })),
);

export function opponentById(id: string): Opponent | undefined {
  return STORY_FIGHTS.find((f) => f.opponent.id === id)?.opponent;
}
